import { Outlet, useLocation } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import PageWrapper from "../components/global/PageWrapper";

import SideBar from "../components/sideBar/SideBar";
import HorizontalNav from "../components/horizontalNav/HorizontalNav";
import { Box, useMediaQuery, useTheme } from "@mui/material";
import Navbar from "../components/navbar/Navbar";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSettingsContext } from "../setting/SettingsContext";

function Layout() {
    const { i18n } = useTranslation();
    const muiTheme = useTheme();
    const { themeLayout, onChangeLayout } = useSettingsContext();
    const location = useLocation();


    // Below md the horizontal bar gets cramped, so we always fall back to
    // the (collapsed) sidebar on small screens regardless of themeLayout.
    const isBelowMd = useMediaQuery(muiTheme.breakpoints.down('md'));
    const isHorizontalRequested = themeLayout === 'horizontal';
    const isHorizontalActive = isHorizontalRequested && !isBelowMd;

    // The sidebar's `open` flag is fully derived from settings.themeLayout
    // when one of the sidebar-shaped layouts is active. The setOpen shim
    // forwarded to SideBar writes back through onChangeLayout, keeping
    // SettingsContext as the single source of truth.
    const open = themeLayout !== 'mini';
    const setOpen = (next) => {
        const resolved = typeof next === 'function' ? next(open) : next;
        onChangeLayout(resolved ? 'vertical' : 'mini');
    };

    const [lng, setLng] = useState(i18n.language);

    useEffect(() => {
        const onLanguageChanged = (lng) => {
            setLng(lng);
        };
        i18n.on('languageChanged', onLanguageChanged);

        return () => {
            i18n.off('languageChanged', onLanguageChanged);
        };
    }, [i18n]);

    const scrollbarSx = {
        '&::-webkit-scrollbar': { width: 6 },
        '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
        '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(148,163,184,0.6)',
            borderRadius: 999,
        },
        '&:hover::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(148,163,184,0.9)',
        },
        scrollbarWidth: 'thin',
        scrollbarColor: 'rgba(148,163,184,0.8) transparent',
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', direction: lng === 'ar' ? 'rtl' : 'ltr' }}>
            <Navbar />

            {/* Horizontal nav slot — only when active. */}
            {isHorizontalActive && <HorizontalNav />}

            <Box
                sx={
                    isHorizontalActive
                        ? {
                            // Single-column layout — horizontal bar already
                            // covers navigation, so no sidebar grid here.
                            display: 'flex',
                            flexDirection: 'column',
                            flex: 1,
                            minHeight: 0,
                        }
                        : {
                            display: 'grid',
                            gridTemplateColumns: open
                                ? `2fr 10fr`
                                : { md: `1fr 11fr`, xs: `2fr 10fr` },
                            flex: 1,
                            minHeight: 0,
                        }
                }
            >
                {!isHorizontalActive && (
                    <Box
                        sx={{
                            overflowY: 'auto',
                            height: '100%',
                            ...scrollbarSx,
                        }}
                    >
                        <SideBar open={open} setOpen={setOpen} direction={lng === 'ar' ? 'rtl' : 'ltr'} />
                    </Box>
                )}

                <Box
                    sx={{
                        height: '100%',
                        overflowY: 'auto',
                        display: 'flex',
                        flexDirection: 'column',
                        position: 'relative', // for page wrapper
                        ...scrollbarSx,
                    }}
                >
                    <AnimatePresence mode="wait">
                        <PageWrapper key={location.pathname}>
                            <Outlet />
                        </PageWrapper>
                    </AnimatePresence>
                </Box>
            </Box>
        </Box>
    );
}

export default Layout;
